/**
 * Locale controller for the web app (ARCHITECTURE.md §8).
 *
 * `@richrich/i18n` is platform-agnostic and never touches the DOM; this module is
 * the web host's persistence + `<html lang>` adapter around it. The default is
 * Korean and there is deliberately NO `navigator.language` detection — the choice
 * is explicit and sticky (localStorage `rr-locale`). `<html lang>` is kept in sync
 * so the browser, screen readers, and CSS `:lang()` see the active language.
 */
import { DEFAULT_LOCALE, type Locale } from "@richrich/i18n";

const STORAGE_KEY = "rr-locale";

/** Read the persisted locale; defaults to Korean when unset or invalid. */
export function storedLocale(): Locale {
  if (typeof localStorage === "undefined") return DEFAULT_LOCALE;
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "en" || v === "ko" ? v : DEFAULT_LOCALE;
}

/** Reflect the active locale onto `<html lang>` (no-op outside a document). */
export function syncDocumentLang(locale: Locale): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

/** Persist a locale choice and sync `<html lang>`. */
export function persistLocale(locale: Locale): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Storage may be unavailable (private mode); the in-memory choice still applies.
  }
  syncDocumentLang(locale);
}

/** Ensure `<html lang>` reflects the persisted locale on boot (idempotent). */
export function initLocale(): void {
  syncDocumentLang(storedLocale());
}
